// Overview / home page (RFC-0009 Task 1.4, §2/§6).
//
// Reads exclusively from the RFC-0006 live read API via TanStack Query hooks
// (ADR-0007): summary stat tiles from `useProjectsHealthSummary` and a recent
// project grid from `useProjects`. Renders the §6 states — skeleton on load, an
// envelope-aware error panel, and the "no repositories connected" empty state
// with the shared connect CTA (RFC-0002). No static/mock content remains.

import {
  ArrowRight,
  GithubLogo,
  Books,
  WarningOctagon,
  ArrowsClockwise,
  Clock,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState } from '@/components/ui/empty-state';
import { SectionHeader } from '@/components/ui/section-header';
import { StatTilesSkeleton, CardGridSkeleton } from '@/components/ui/query-skeletons';
import { ErrorPanel } from '@/components/ui/error-panel';
import { ProjectCard } from '@/components/projects/project-card';
import { useProjects, useProjectsHealthSummary } from '@/lib/queries/projects';
import { useConnectRepo } from '@/context/connect-repo';

const RECENT_LIMIT = 6;

export function OverviewPage() {
  const navigate = useNavigate();
  const { open } = useConnectRepo();
  const summary = useProjectsHealthSummary();
  const projects = useProjects({ perPage: RECENT_LIMIT });

  const items = projects.data?.data ?? [];
  const noProjects =
    projects.isSuccess && items.length === 0 && (summary.data?.projects ?? 0) === 0;

  return (
    <>
      {/* Hero */}
      <section className="stagger pb-2">
        <Badge variant="info" dot className="mb-5">
          Internal documentation portal
        </Badge>
        <h1 className="max-w-2xl text-3xl font-light leading-[1.08] tracking-tight text-text sm:text-4xl">
          Every team&rsquo;s docs,{' '}
          <span
            style={{
              background: 'var(--nmblr-gradient)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            one portal
          </span>
          .
        </h1>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
          nmblr-docs aggregates documentation straight from your GitHub repos,
          indexes it for instant search, and keeps it in sync on every push.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Button size="md" className="min-h-[44px]" onClick={() => navigate('/repositories')}>
            Browse repositories
            <ArrowRight size={15} weight="bold" />
          </Button>
          <Button
            variant="secondary"
            size="md"
            className="min-h-[44px]"
            onClick={() => open()}
          >
            <GithubLogo size={16} />
            Connect a repository
          </Button>
        </div>
      </section>

      {/* Summary stat tiles */}
      <section className="mt-10">
        {summary.isLoading ? (
          <StatTilesSkeleton />
        ) : summary.isError ? (
          <ErrorPanel error={summary.error} onRetry={() => void summary.refetch()} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Repositories"
              value={summary.data?.projects ?? 0}
              icon={<Books size={16} weight="duotone" />}
            />
            <StatCard
              label="Manifest broken"
              value={summary.data?.manifestBroken ?? 0}
              icon={<WarningOctagon size={16} weight="duotone" />}
            />
            <StatCard
              label="Sync failed"
              value={summary.data?.syncFailed ?? 0}
              icon={<ArrowsClockwise size={16} weight="duotone" />}
            />
            <StatCard
              label="Stale"
              value={summary.data?.stale ?? 0}
              icon={<Clock size={16} weight="duotone" />}
            />
          </div>
        )}
      </section>

      {/* Recent repositories */}
      <section className="mt-2">
        <SectionHeader
          title="Repositories"
          sub={`· ${items.length ? 'most recently synced' : 'connect a repo to get started'}`}
          action={
            <button
              type="button"
              onClick={() => navigate('/repositories')}
              className="inline-flex min-h-[44px] items-center font-medium text-accent hover:underline"
            >
              View all
            </button>
          }
        />

        {projects.isLoading ? (
          <CardGridSkeleton count={RECENT_LIMIT} />
        ) : projects.isError ? (
          <ErrorPanel error={projects.error} onRetry={() => void projects.refetch()} />
        ) : noProjects ? (
          <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
            <EmptyState
              icon={<GithubLogo size={22} />}
              title="No repositories connected yet"
              description="Add a .nmblr.portal.yaml manifest to a repo, then connect it here. The sync worker will ingest its docs on the next push."
              action={
                <Button size="md" className="min-h-[44px]" onClick={() => open()}>
                  <GithubLogo size={16} />
                  Connect a repository
                </Button>
              }
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {items.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
